import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Heart, ImageOff, Image, Loader2 } from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { getInitialProfileImageUrl } from '@/services/profileImage';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { isValidUuid, formatStorageUrl, getTimestampedImagePath } from '@/services/profileImage/imageUtils';
import { EscortProfile } from '@/types';

interface ProfileCardHeaderProps {
  profileId: string;
  imageUrl: string;
  displayName: string;
  isVerified: boolean;
  isOnline: boolean;
  isTouring?: boolean;
  isFavorite: boolean;
  isHovered: boolean;
  isLoadingImage?: boolean;
  handleToggleFavorite: (e: React.MouseEvent) => void;
  storageAvailable?: boolean | null;
}

const ProfileCardHeader: React.FC<ProfileCardHeaderProps> = ({
  profileId,
  imageUrl,
  displayName,
  isVerified,
  isOnline,
  isTouring,
  isFavorite,
  isHovered,
  isLoadingImage: isGeneratingImage,
  handleToggleFavorite,
  storageAvailable,
}) => {
  const { t } = useLanguage();
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const [imageError, setImageError] = useState(false);
  const [finalImageUrl, setFinalImageUrl] = useState<string>(imageUrl || '');
  
  useEffect(() => {
    setIsImageLoading(true);
    setImageError(false);
    
    if (!imageUrl) {
      // If no image URL is provided, use a placeholder
      setFinalImageUrl(getInitialProfileImageUrl({ displayName } as EscortProfile));
      setIsImageLoading(false);
      return;
    }
    
    // Vérifier si l'URL est déjà complète (contient https:// ou http://)
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      console.log(`[${displayName}] URL complète utilisée:`, imageUrl);
      setFinalImageUrl(imageUrl);
      setIsImageLoading(false);
      return;
    }
    
    // Pour les profils avec ID, utiliser directement le chemin avec timestamp connu
    if (profileId) {
      const timestampedPath = getTimestampedImagePath(profileId);
      const formattedUrl = formatStorageUrl(timestampedPath);
      console.log(`[${displayName}] Chemin avec timestamp utilisé:`, formattedUrl);
      setFinalImageUrl(formattedUrl);
      setIsImageLoading(false);
      return;
    }
    
    // Fallback si aucune des conditions précédentes n'est remplie
    console.log(`[${displayName}] Fallback - URL fournie directement formatée:`, imageUrl);
    setFinalImageUrl(formatStorageUrl(imageUrl));
    setIsImageLoading(false);
  }, [imageUrl, displayName, profileId]);
  
  const showLoadingState = isImageLoading || isGeneratingImage;
  const showStorageError = storageAvailable === false;

  return (
    <div className="relative aspect-[3/4] overflow-hidden group">
      {/* Main container with fixed background */}
      <div className="w-full h-full bg-gradient-to-b from-gray-200 to-gray-300">
        {/* Image container with hover effect */}
        <div 
          className="w-full h-full"
          style={{ 
            position: 'relative',
            zIndex: 1
          }}
        >
          {(showLoadingState) ? (
            <Skeleton className="w-full h-full" />
          ) : finalImageUrl && !imageError ? (
            <img
              src={finalImageUrl}
              alt={displayName}
              className="w-full h-full object-cover transition-transform duration-500"
              style={{ transform: isHovered ? 'scale(1.05)' : 'scale(1)' }}
              onError={(e) => {
                console.error(`Image load error for ${displayName}:`, finalImageUrl);
                setImageError(true);
                setFinalImageUrl(getInitialProfileImageUrl({ displayName } as EscortProfile));
              }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
              {showStorageError ? (
                <>
                  <Image size={48} className="mb-2 text-gray-500" />
                  <span className="text-sm text-gray-700 font-medium mb-1">{displayName}</span>
                  <span className="text-xs text-gray-500">Storage unavailable</span>
                </>
              ) : (
                <>
                  <ImageOff size={48} className="mb-2 text-gray-500" />
                  <span className="text-sm text-gray-700">{displayName}</span>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Static overlay that changes opacity on hover */}
        <div 
          className="absolute inset-0 bg-black transition-opacity duration-300 pointer-events-none"
          style={{ 
            opacity: isHovered ? 0.2 : 0,
            zIndex: 2
          }}
        />
      
        {/* Status badges */}
        <div className="absolute top-2 left-2 flex flex-col space-y-1 z-10">
          {isVerified && (
            <Badge className="bg-green-500 hover:bg-green-600 flex items-center space-x-1">
              <Check size={12} className="mr-1" />
              <span>{t('Verified')}</span>
            </Badge>
          )}
          {isOnline && (
            <Badge className="bg-blue-500 hover:bg-blue-600">{t('Online')}</Badge>
          )}
          {isTouring && (
            <Badge className="bg-purple-light hover:bg-purple-DEFAULT">{t('Touring')}</Badge>
          )}
        </div>
      
        {/* Favorite button */}
        <div className="absolute top-2 right-2 flex flex-col gap-2 z-10">
          <Button
            variant="ghost"
            size="icon"
            className={`rounded-full bg-background/50 backdrop-blur-sm hover:bg-background/70 ${
              isFavorite ? 'text-red-500 hover:text-red-600' : 'text-white hover:text-primary'
            }`}
            onClick={handleToggleFavorite}
            aria-label={isFavorite ? t('profile.unfavorite') : t('profile.favorite')}
          >
            <Heart size={20} fill={isFavorite ? "currentColor" : "none"} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCardHeader;
